import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchPhonemes, createPhoneme, deletePhoneme, fetchLessons, createLesson, deleteLesson, fetchGames, createGame, deleteGame } from "../lib/api";

type Tab = "phonemes" | "lessons" | "games";

export function ContentPage() {
  const [tab, setTab] = useState<Tab>("phonemes");

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Content</h1>

      <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
        {(["phonemes", "lessons", "games"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-md px-4 py-2 text-sm font-medium capitalize ${
              tab === t ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "phonemes" && <PhonemesTab />}
      {tab === "lessons" && <LessonsTab />}
      {tab === "games" && <GamesTab />}
    </div>
  );
}

function PhonemesTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["phonemes"], queryFn: fetchPhonemes });
  const add = useMutation({
    mutationFn: (body: Record<string, unknown>) => createPhoneme(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["phonemes"] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deletePhoneme(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["phonemes"] }),
  });

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ grapheme: "", phoneme: "", phase: "2", phoneme_order: "1", example_words: "", is_vowel: false });

  const items = (data?.data ?? []) as Record<string, unknown>[];

  const handleAdd = () => {
    add.mutate({
      grapheme: form.grapheme,
      phoneme: form.phoneme,
      phase: parseInt(form.phase, 10),
      phoneme_order: parseInt(form.phoneme_order, 10),
      example_words: form.example_words.split(",").map((w) => w.trim()).filter(Boolean),
      is_vowel: form.is_vowel,
    });
    setShowForm(false);
    setForm({ grapheme: "", phoneme: "", phase: "2", phoneme_order: "1", example_words: "", is_vowel: false });
  };

  return (
    <div className="space-y-3">
      <button onClick={() => setShowForm(!showForm)} className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600">
        {showForm ? "Cancel" : "+ Add Phoneme"}
      </button>

      {showForm && (
        <div className="grid grid-cols-2 gap-3 rounded-xl border bg-white p-4 md:grid-cols-3">
          <input placeholder="Grapheme (e.g. sh)" value={form.grapheme} onChange={(e) => setForm({ ...form, grapheme: e.target.value })} className="rounded border px-3 py-2 text-sm" />
          <input placeholder="Phoneme (e.g. /ʃ/)" value={form.phoneme} onChange={(e) => setForm({ ...form, phoneme: e.target.value })} className="rounded border px-3 py-2 text-sm" />
          <input placeholder="Phase" type="number" value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })} className="rounded border px-3 py-2 text-sm" />
          <input placeholder="Order" type="number" value={form.phoneme_order} onChange={(e) => setForm({ ...form, phoneme_order: e.target.value })} className="rounded border px-3 py-2 text-sm" />
          <input placeholder="Example words (comma separated)" value={form.example_words} onChange={(e) => setForm({ ...form, example_words: e.target.value })} className="col-span-2 rounded border px-3 py-2 text-sm md:col-span-1" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_vowel} onChange={(e) => setForm({ ...form, is_vowel: e.target.checked })} /> Vowel
          </label>
          <button onClick={handleAdd} disabled={add.isPending || !form.grapheme} className="rounded bg-forest-500 px-4 py-2 text-sm font-semibold text-white hover:bg-forest-700 disabled:opacity-50 md:col-start-3">
            Save
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b bg-gray-50">
            <tr>
              <th className="px-4 py-3 font-medium text-gray-600">Grapheme</th>
              <th className="px-4 py-3 font-medium text-gray-600">Phoneme</th>
              <th className="px-4 py-3 font-medium text-gray-600">Phase</th>
              <th className="px-4 py-3 font-medium text-gray-600">Example Words</th>
              <th className="px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
            ) : items.map((p) => (
              <tr key={String(p.id)}>
                <td className="px-4 py-3 font-mono font-semibold">{String(p.grapheme)}</td>
                <td className="px-4 py-3">{String(p.phoneme)}</td>
                <td className="px-4 py-3">{String(p.phase)}</td>
                <td className="px-4 py-3 text-gray-500">{Array.isArray(p.example_words) ? p.example_words.join(", ") : "—"}</td>
                <td className="px-4 py-3">
                  <button onClick={() => { if (confirm("Delete this phoneme?")) remove.mutate(String(p.id)); }} className="text-red-600 hover:text-red-800 text-xs font-medium">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LessonsTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["lessons"], queryFn: fetchLessons });
  const add = useMutation({
    mutationFn: (body: Record<string, unknown>) => createLesson(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lessons"] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteLesson(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lessons"] }),
  });

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", phase: "2", lesson_order: "1", phonemes_covered: "", description: "" });

  const items = (data?.data ?? []) as Record<string, unknown>[];

  const handleAdd = () => {
    add.mutate({
      title: form.title,
      phase: parseInt(form.phase, 10),
      lesson_order: parseInt(form.lesson_order, 10),
      phonemes_covered: form.phonemes_covered.split(",").map((w) => w.trim()).filter(Boolean),
      description: form.description || undefined,
    });
    setShowForm(false);
    setForm({ title: "", phase: "2", lesson_order: "1", phonemes_covered: "", description: "" });
  };

  return (
    <div className="space-y-3">
      <button onClick={() => setShowForm(!showForm)} className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600">
        {showForm ? "Cancel" : "+ Add Lesson"}
      </button>

      {showForm && (
        <div className="grid grid-cols-2 gap-3 rounded-xl border bg-white p-4 md:grid-cols-3">
          <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="col-span-2 rounded border px-3 py-2 text-sm md:col-span-1" />
          <input placeholder="Phase" type="number" value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })} className="rounded border px-3 py-2 text-sm" />
          <input placeholder="Order" type="number" value={form.lesson_order} onChange={(e) => setForm({ ...form, lesson_order: e.target.value })} className="rounded border px-3 py-2 text-sm" />
          <input placeholder="Phonemes covered (comma separated)" value={form.phonemes_covered} onChange={(e) => setForm({ ...form, phonemes_covered: e.target.value })} className="col-span-2 rounded border px-3 py-2 text-sm" />
          <input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded border px-3 py-2 text-sm" />
          <button onClick={handleAdd} disabled={add.isPending || !form.title} className="rounded bg-forest-500 px-4 py-2 text-sm font-semibold text-white hover:bg-forest-700 disabled:opacity-50">
            Save
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b bg-gray-50">
            <tr>
              <th className="px-4 py-3 font-medium text-gray-600">Title</th>
              <th className="px-4 py-3 font-medium text-gray-600">Phase</th>
              <th className="px-4 py-3 font-medium text-gray-600">Phonemes</th>
              <th className="px-4 py-3 font-medium text-gray-600">Games</th>
              <th className="px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
            ) : items.map((l) => (
              <tr key={String(l.id)}>
                <td className="px-4 py-3 font-medium">{String(l.title)}</td>
                <td className="px-4 py-3">{String(l.phase)}</td>
                <td className="px-4 py-3 text-gray-500">{Array.isArray(l.phonemes_covered) ? l.phonemes_covered.join(", ") : "—"}</td>
                <td className="px-4 py-3">{Array.isArray(l.games) ? l.games.length : 0}</td>
                <td className="px-4 py-3">
                  <button onClick={() => { if (confirm("Delete this lesson?")) remove.mutate(String(l.id)); }} className="text-red-600 hover:text-red-800 text-xs font-medium">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GamesTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["games"], queryFn: fetchGames });
  const add = useMutation({
    mutationFn: (body: Record<string, unknown>) => createGame(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["games"] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteGame(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["games"] }),
  });

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", game_type: "letter_match", lesson_id: "", config: "{}" });

  const items = (data?.data ?? []) as Record<string, unknown>[];

  const handleAdd = () => {
    let config = {};
    try { config = JSON.parse(form.config); } catch { /* use empty */ }
    add.mutate({
      title: form.title,
      game_type: form.game_type,
      lesson_id: form.lesson_id || undefined,
      config,
    });
    setShowForm(false);
    setForm({ title: "", game_type: "letter_match", lesson_id: "", config: "{}" });
  };

  return (
    <div className="space-y-3">
      <button onClick={() => setShowForm(!showForm)} className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600">
        {showForm ? "Cancel" : "+ Add Game"}
      </button>

      {showForm && (
        <div className="grid grid-cols-2 gap-3 rounded-xl border bg-white p-4 md:grid-cols-3">
          <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded border px-3 py-2 text-sm" />
          <select value={form.game_type} onChange={(e) => setForm({ ...form, game_type: e.target.value })} className="rounded border px-3 py-2 text-sm">
            <option value="letter_match">Letter Match</option>
            <option value="word_build">Word Build</option>
            <option value="sound_sort">Sound Sort</option>
            <option value="picture_match">Picture Match</option>
            <option value="phoneme_pop">Phoneme Pop</option>
            <option value="rhyme_match">Rhyme Match</option>
            <option value="missing_letter">Missing Letter</option>
            <option value="word_treasure">Word Treasure</option>
          </select>
          <input placeholder="Lesson ID" value={form.lesson_id} onChange={(e) => setForm({ ...form, lesson_id: e.target.value })} className="rounded border px-3 py-2 text-sm" />
          <textarea placeholder='Config JSON e.g. {}' value={form.config} onChange={(e) => setForm({ ...form, config: e.target.value })} className="col-span-2 rounded border px-3 py-2 text-sm" rows={3} />
          <button onClick={handleAdd} disabled={add.isPending || !form.title} className="rounded bg-forest-500 px-4 py-2 text-sm font-semibold text-white hover:bg-forest-700 disabled:opacity-50">
            Save
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b bg-gray-50">
            <tr>
              <th className="px-4 py-3 font-medium text-gray-600">Title</th>
              <th className="px-4 py-3 font-medium text-gray-600">Type</th>
              <th className="px-4 py-3 font-medium text-gray-600">Lesson</th>
              <th className="px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
            ) : items.map((g) => (
              <tr key={String(g.id)}>
                <td className="px-4 py-3 font-medium">{String(g.title)}</td>
                <td className="px-4 py-3 capitalize">{String(g.game_type ?? "").replace(/_/g, " ")}</td>
                <td className="px-4 py-3 text-gray-500 text-xs font-mono">{String(g.lesson_id ?? "—").slice(0, 8)}</td>
                <td className="px-4 py-3">
                  <button onClick={() => { if (confirm("Delete this game?")) remove.mutate(String(g.id)); }} className="text-red-600 hover:text-red-800 text-xs font-medium">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
